/**
 * Quantity Stepper
 *
 * Handles increment/decrement buttons injected into WooCommerce quantity wrappers
 * (.quantity, .wc-sc-input) via the woocommerce_before/after_quantity_input_field hooks.
 *
 * Buttons carry the class `quantity-stepper--inactive` when the current value is at
 * the input's min or max boundary, allowing CSS to style them as disabled.
 */

/**
 * Toggle `quantity-stepper--inactive` on the minus/plus buttons based on whether
 * the current input value has reached the min or max boundary.
 *
 * @param {Element} wrapper - The .quantity or .wc-sc-input container element.
 */
function updateStepperState(wrapper) {
	const input = wrapper.querySelector('input[type="number"], input[type="text"]');
	if (!input) {
		return;
	}

	const val = parseFloat(input.value) || 0;
	const min = parseFloat(input.getAttribute('min')) || 0;
	const max = input.getAttribute('max') ? parseFloat(input.getAttribute('max')) : Infinity;

	const minus = wrapper.querySelector('.quantity-stepper--minus');
	const plus = wrapper.querySelector('.quantity-stepper--plus');

	if (minus) {
		minus.classList.toggle('quantity-stepper--inactive', val <= min);
	}
	if (plus) {
		plus.classList.toggle('quantity-stepper--inactive', max !== Infinity && val >= max);
	}
}

/**
 * Run updateStepperState on every quantity wrapper in the document.
 * Called on DOMContentLoaded and after WooCommerce variation events.
 */
function initStepperStates() {
	document.querySelectorAll('.quantity, .wc-sc-input').forEach(updateStepperState);
}

document.addEventListener('DOMContentLoaded', initStepperStates);

// Increment or decrement the quantity when a stepper button is clicked.
document.addEventListener('click', function (e) {
	const btn = e.target.closest('.quantity-stepper');
	if (!btn) {
		return;
	}

	const wrapper = btn.closest('.quantity, .wc-sc-input');
	if (!wrapper) {
		return;
	}

	const input = wrapper.querySelector('input[type="number"], input[type="text"]');
	if (!input) {
		return;
	}

	const isPlus = btn.classList.contains('quantity-stepper--plus');
	const min = parseFloat(input.getAttribute('min')) || 0;
	const max = input.getAttribute('max') ? parseFloat(input.getAttribute('max')) : Infinity;
	const step = parseFloat(input.getAttribute('step')) || 1;
	let val = parseFloat(input.value) || 0;

	if (isPlus) {
		val = max !== Infinity ? Math.min(max, val + step) : val + step;
	} else {
		val = Math.max(min, val - step);
	}

	input.value = val;
	// Trigger WooCommerce listeners that watch for quantity changes.
	input.dispatchEvent(new Event('change', { bubbles: true }));
	input.dispatchEvent(new Event('input', { bubbles: true }));

	updateStepperState(wrapper);
});

// Re-evaluate button states when the value is changed by direct input.
document.addEventListener('change', function (e) {
	if (!e.target.matches('.quantity input, .wc-sc-input input')) {
		return;
	}
	updateStepperState(e.target.closest('.quantity, .wc-sc-input'));
});

// Re-evaluate after WooCommerce swaps in a new variation (min/max may change).
document.addEventListener('found_variation', initStepperStates);
document.addEventListener('reset_data', initStepperStates);
